import React, { useCallback, useMemo } from 'react';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WriteBackToGreenhouseAuthMethod } from 'libs/end-to-end/src/types/databases/workflows/strategies/greenhouse-write-back';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';
import { DataSetSecretSelector } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSecretSelector';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { useConnectionsConfiguration } from 'apps/sigma2-ui/src/lib/hooks/useConnectionsConfiguration';

function GreenhouseConnectionSelector({
  organizationId,
}: {
  organizationId: string;
}) {
  const connections = useConnectionsConfiguration(organizationId);
  const isAvailable = useMemo(() => {
    if (connections.state !== 'succeeded' || !connections.config?.isEnabled) {
      return false;
    }
    const gh = connections.config.connections?.greenhouse;
    return gh?.isEnabled === true && gh?.installation?.isInstalled === true;
  }, [connections]);

  return (
    <div className="space-y-4">
      {connections.state === 'loading' && (
        <p className="text-gray-600">Loading connections...</p>
      )}
      {connections.state === 'succeeded' && isAvailable && (
        <p className="text-sm text-gray-600">
          Using the organization&apos;s Greenhouse connection.
        </p>
      )}
      {connections.state === 'succeeded' && !isAvailable && (
        <p className="text-gray-600">
          No Greenhouse connection is configured for this organization.
          Configure it in Organization Settings → Connections.
        </p>
      )}
      {connections.state === 'failed' && (
        <p className="text-red-600">
          {connections.errorMessage ?? 'Failed to load connections.'}
        </p>
      )}
    </div>
  );
}

export interface ConfigureWriteBackGreenhouseAuthMethodProps
  extends ConfigureWorkflowBaseProps {
  authMethod: WriteBackToGreenhouseAuthMethod | null;

  updateAuthMethod: (
    authMethod: WriteBackToGreenhouseAuthMethod | null,
  ) => void;
}

export const ConfigureWriteBackGreenhouseAuthMethod = ({
  authMethod,
  updateAuthMethod,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureWriteBackGreenhouseAuthMethodProps) => {
  const setSelectedMethod = useCallback(
    (selected: 'none' | WriteBackToGreenhouseAuthMethod['type']) => {
      if (selected === 'none') {
        updateAuthMethod(null);
      } else if (selected !== authMethod?.type) {
        if (selected === 'connection') {
          updateAuthMethod({ type: 'connection' });
        } else {
          updateAuthMethod({
            type: selected,
          });
        }
      }
    },
    [authMethod, updateAuthMethod],
  );

  const selectedMethod = useMemo(() => {
    return authMethod?.type ?? 'none';
  }, [authMethod]);

  const updateSecretReference = useCallback(
    (secretReference: SecretReference) => {
      updateAuthMethod({
        type: 'api',
        ...(authMethod?.type === 'api' ? authMethod : {}),
        apiKeySecretReference: secretReference,
      });
    },
    [authMethod, updateAuthMethod],
  );

  const currentSecretReference = useMemo(
    () =>
      authMethod?.type === 'api'
        ? (authMethod.apiKeySecretReference ?? null)
        : null,
    [authMethod],
  );

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">
        Configure Greenhouse Auth Method
      </h2>
      <p className="text-gray-600">
        Select and configure the authentication method for Greenhouse.
      </p>
      <SelectBoxWithSearch
        type="single"
        list={[
          { optionLabel: 'None', optionValue: 'none' },
          { optionLabel: 'Merge', optionValue: 'merge' },
          { optionLabel: 'API Based', optionValue: 'api' },
          { optionLabel: 'Connection', optionValue: 'connection' },
        ]}
        label="Select an auth method"
        selected={selectedMethod}
        setSelected={setSelectedMethod}
      />

      {authMethod?.type === 'merge' && (
        <WorkflowDataSetSelector
          dataSetId={authMethod?.dataSetId ?? null}
          title="Select a merge ATS data set"
          listDataSetsPattern={listDataSetsPattern}
          setDataSetId={(dataSetId) => {
            updateAuthMethod({
              ...authMethod,
              dataSetId: dataSetId ?? undefined,
            });
          }}
          filterFn={isMergeAtsIntegrationDataSet}
        />
      )}

      {authMethod?.type === 'connection' && (
        <GreenhouseConnectionSelector organizationId={organizationId} />
      )}

      {authMethod?.type === 'api' && (
        <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold">
            Configure Greenhouse API Auth Method
          </h2>
          <p className="text-gray-600">
            Select the API key secret for Greenhouse integration.
          </p>
          <DataSetSecretSelector
            secretReference={currentSecretReference}
            setSecretReference={updateSecretReference}
            organizationId={organizationId}
            label="Select the API key secret"
            listSecretsPattern={listSecretsPattern}
          />
        </div>
      )}
    </div>
  );
};
